import {
  AllpayUserDTO,
  TravellerWithCollectionMembershipsDTO,
  useGetAllpayUser,
  useGetDepartmentByID,
} from '@/api/trafikportalen';
import { FormSection } from '../FormSection';
import { FormattedMessage, useIntl } from 'react-intl';
import { DialogWindow } from '../Dialog';
import { useMemo, useState } from 'react';
import Button from '../core/Button';
import { AllpayCreateForm } from './AllpayCreateForm';
import IconButton from '../core/IconButton';
import { IconEdit, IconInfo, IconSmartphoneDevice, IconTrash } from '@/icons';
import { AllpayUserDeleteForm } from './AllpayUserDeleteForm';
import { AllpayUserUpdateForm } from './AllpayUserUpdateForm';
import { getTravellerStatus } from '@/helpers/status';
import {
  hasAnyMembershipDepartmentWriteAccess,
  hasDepartmentWriteAccess,
} from '@/helpers/departmentPermissions';
import { useListTravellerAllpayUsers } from '@/hooks/useTravellerCreditInfo';
import { formatDate, formatDateTime } from '@/helpers/formatting';
import { checkUserPermissions } from '../Auth/checkUserPermissions';

interface Props {
  traveller: TravellerWithCollectionMembershipsDTO;
}

export const AllpayUsers = ({ traveller }: Props) => {
  const { formatMessage, locale } = useIntl();
  const { users: allpayUsers } = useListTravellerAllpayUsers(traveller);

  const [open, setOpen] = useState(false);

  const totalCredit = useMemo(() => {
    return allpayUsers?.reduce((acc, user) => acc + user.credit, 0) ?? 0;
  }, [allpayUsers]);

  const status = getTravellerStatus(traveller);

  const departmentsWithoutAllpay = useMemo(() => {
    return traveller.memberships.filter(
      (membership) =>
        membership.active &&
        !allpayUsers?.some(
          (user) => user.department_id === membership.department_id,
        ),
    );
  }, [traveller.memberships, allpayUsers]);

  const hasDepartmentsWithoutAllpay = useMemo(() => {
    return departmentsWithoutAllpay.some((membership) =>
      hasDepartmentWriteAccess(membership.department_id),
    );
  }, [departmentsWithoutAllpay]);

  return (
    <>
      <FormSection
        id="allpay-users"
        title={
          <div className="flex flex-wrap items-center gap-x-2">
            <IconSmartphoneDevice className="size-8 min-w-8 text-allpay" />
            <FormattedMessage defaultMessage="Tjänsteresa i app" />
            <DialogWindow
              title={formatMessage({ defaultMessage: 'Tjänsteresa i app' })}
              content={
                <div className="flex flex-col gap-2">
                  <p>
                    <FormattedMessage defaultMessage="Ge resenärer i organisationen möjlighet använda faktura som betalmetod i lokaltrafikappen för sina tjänsteresor." />
                  </p>
                  <p>
                    <FormattedMessage defaultMessage="Den kredit som sätts är hur mycket resenären kan resa för under varje kalendermånad." />
                  </p>
                  <p>
                    <FormattedMessage defaultMessage="Det går bara att ha en tjänsteresa i appen aktivt per avdelning." />
                  </p>
                  <p>
                    <FormattedMessage defaultMessage="För att kunna tilldela tjänsteresa i appen behövs resenärens mobilnummer. Detta nummer används för att skicka sms till resenären om hur de aktiverar tjänsteresa i appen." />
                  </p>
                  <p>
                    <FormattedMessage defaultMessage="När resenären ska göra ett köp i appen kan företagsfaktura användas och de tvingas då att ange syftet för köpet." />
                  </p>
                </div>
              }
              dialogTrigger={
                <IconButton
                  aria-label={formatMessage({
                    defaultMessage: 'Visa information om tjänsteresa i app',
                  })}
                  icon={<IconInfo />}
                  variant="ghost"
                  className="ml-auto"
                />
              }
            />
          </div>
        }
      >
        {status === 'inactive' ? (
          <i className="pb-2 text-text-700">
            <FormattedMessage defaultMessage="Resenären är inaktiv och kan därmed inte ha tjänsteresa i appen" />
          </i>
        ) : !allpayUsers || allpayUsers.length === 0 ? (
          <i className="pb-2 text-text-700">
            <FormattedMessage defaultMessage="Ingen tjänsteresa i appen för den här resenären" />
          </i>
        ) : null}
        {allpayUsers &&
          allpayUsers.length > 1 && ( // Intentionally 1, since the total is the same as the allpay user otherwise
            <p className="border-b border-b-background-200 pb-2 text-text-800">
              <FormattedMessage defaultMessage="Total kredit i appen:" />{' '}
              <span className="font-semibold">
                {totalCredit.toLocaleString(locale)}
              </span>{' '}
              <FormattedMessage defaultMessage="SEK" />
            </p>
          )}
        <div className="flex flex-col">
          {allpayUsers?.map((user) => (
            <AllpayUser key={user.id} traveller={traveller} user={user} />
          ))}
        </div>
        {hasAnyMembershipDepartmentWriteAccess(traveller) &&
          hasDepartmentsWithoutAllpay && (
            <Button
              onClick={() => setOpen(true)}
              disabled={status === 'inactive'}
            >
              <FormattedMessage defaultMessage="Lägg till tjänsteresa i app" />
            </Button>
          )}
        {status !== 'inactive' && !hasDepartmentsWithoutAllpay ? (
          <i className="text-text-700">
            {checkUserPermissions({ permission: 'organization:editor' }) ? (
              <FormattedMessage defaultMessage="Resenären har redan tjänsteresa i app för alla sina avdelningar." />
            ) : (
              <FormattedMessage defaultMessage="Resenären har redan tjänsteresa i app för alla sina avdelningar (som du har skrivrättighet för)." />
            )}
          </i>
        ) : null}
      </FormSection>
      <DialogWindow
        title={formatMessage({
          defaultMessage: 'Lägg till tjänsteresa i app',
        })}
        content={
          <AllpayCreateForm traveller={traveller} onOpenChange={setOpen} />
        }
        onOpenChange={setOpen}
        open={open}
      />
    </>
  );
};

const AllpayUser = ({
  traveller,
  user,
}: {
  traveller: TravellerWithCollectionMembershipsDTO;
  user: AllpayUserDTO;
}) => {
  const { data: department } = useGetDepartmentByID(user.department_id);
  const { formatMessage, locale } = useIntl();
  const { data: allpayUser } = useGetAllpayUser(user.id);

  const [updateOpen, setUpdateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (!department) return null;

  const showUpdatedDate = user.created_at !== user.updated_at;

  return (
    <div className="mb-2 flex gap-2 border-b border-b-background-200 pb-2 last:border-b-0">
      <IconSmartphoneDevice className="size-5 min-w-5" />
      <div className="flex min-w-0 flex-col">
        <div className="flex w-full flex-col sm:flex-row sm:items-center sm:gap-5">
          <p>
            {user.credit.toLocaleString(locale)}{' '}
            <span className="text-text-800">
              <FormattedMessage defaultMessage="SEK" />
            </span>
          </p>
          <p className="min-w-0 flex-1 truncate">{department.name}</p>
        </div>
        <div className="flex flex-wrap gap-x-3 text-text-700">
          <span
            className="whitespace-nowrap"
            title={formatDateTime(user.created_at)}
          >
            <FormattedMessage
              defaultMessage="Skapad {date}"
              values={{ date: formatDate(user.created_at) }}
            />
          </span>
          {showUpdatedDate && (
            <span
              className="whitespace-nowrap"
              title={formatDateTime(user.updated_at)}
            >
              <FormattedMessage
                defaultMessage="Uppdaterad {date}"
                values={{ date: formatDate(user.updated_at) }}
              />
            </span>
          )}
          {allpayUser?.state === 'registered' && (
            <span className="whitespace-nowrap rounded-full bg-yellow-100 px-2 py-1 text-sm text-yellow-800">
              <FormattedMessage defaultMessage="Måste aktiveras inom 7 dagar" />
            </span>
          )}
          {allpayUser?.state === 'expired' && (
            <span className="whitespace-nowrap rounded-full bg-red-100 px-2 py-1 text-sm text-red-800">
              <FormattedMessage defaultMessage="Blev inte aktiverad inom 7 dagar" />
            </span>
          )}
        </div>
      </div>
      {hasDepartmentWriteAccess(user.department_id) && (
        <div className="ml-auto flex gap-1">
          <IconButton
            aria-label={formatMessage({ defaultMessage: 'Redigera' })}
            icon={<IconEdit />}
            variant="ghost"
            onClick={() => setUpdateOpen(true)}
          />
          <IconButton
            aria-label={formatMessage({ defaultMessage: 'Ta bort' })}
            icon={<IconTrash />}
            variant="ghost"
            color="red"
            onClick={() => setDeleteOpen(true)}
          />
        </div>
      )}
      <DialogWindow
        title={formatMessage({ defaultMessage: 'Uppdatera kreditgräns' })}
        content={
          <AllpayUserUpdateForm
            allpayUser={user}
            traveller={traveller}
            onOpenChange={setUpdateOpen}
          />
        }
        onOpenChange={setUpdateOpen}
        open={updateOpen}
      />
      <DialogWindow
        title={formatMessage({ defaultMessage: 'Ta bort tjänsteresekredit' })}
        content={
          <AllpayUserDeleteForm
            allpayUserID={user.id}
            onOpenChange={setDeleteOpen}
          />
        }
        onOpenChange={setDeleteOpen}
        open={deleteOpen}
      />
    </div>
  );
};
